import React from 'react';
import {
  Box,
  Grid,
  Typography,
  Divider,
  Card,
  CardContent,
  Chip
} from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import PersonIcon from '@mui/icons-material/Person';
import LinkIcon from '@mui/icons-material/Link';
import CloudIcon from '@mui/icons-material/Cloud';
import DescriptionIcon from '@mui/icons-material/Description';
import CampaignIcon from '@mui/icons-material/Campaign';
import AdStatistics from './AdStatistics';

const InfoCard = ({ title, children, icon }) => (
  <Card variant="outlined" sx={{ mb: 3, height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        {icon}
        <Typography variant="h6" component="h3" sx={{ ml: 1 }}>
          {title}
        </Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />
      {children}
    </CardContent>
  </Card>
);

const InfoItem = ({ icon, primary, secondary }) => (
  <Box sx={{ display: 'flex', mb: 2, alignItems: 'flex-start' }}>
    <Box sx={{ mr: 1.5, color: 'text.secondary', mt: 0.5 }}>
      {icon}
    </Box>
    <Box>
      {secondary && (
        <Typography variant="body2" color="text.secondary">
          {secondary}
        </Typography>
      )}
      <Typography variant="body1">
        {primary || 'Not provided'}
      </Typography>
    </Box>
  </Box>
);

const AdvertisingDetailsTab = ({ organization }) => {
  if (!organization) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No organization selected
        </Typography>
      </Box>
    );
  }
  
  // Function to check if values are true (handling different formats)
  const isValueTrue = (value) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') return value.toLowerCase() === 'true' || value === '1';
    if (typeof value === 'number') return value !== 0;
    return false;
  };
  
  return (
    <Grid container spacing={3}>
      {/* General Advertising Information */}
      <Grid item xs={12} md={6}>
        <InfoCard 
          title="General Advertising" 
          icon={<CampaignIcon color="primary" />}
        >
          {/* Ads Enabled */}
          <InfoItem 
            icon={<PaymentIcon fontSize="small" />}
            primary={
              organization.adsEnabled !== undefined && organization.adsEnabled !== null
                ? isValueTrue(organization.adsEnabled) ? 'Yes' : 'No'
                : organization.paid !== undefined && organization.paid !== null
                  ? (organization.paid === 0 || organization.paid === '0' || organization.paid === false) ? 'Yes' : 'No'
                  : 'No'
            }
            secondary="Ads Enabled"
          />
          
          {/* Number of Families */}
          {organization.numberOfFamilies !== undefined && organization.numberOfFamilies !== null && (
            <InfoItem 
              icon={<PersonIcon fontSize="small" />}
              primary={organization.numberOfFamilies.toString()}
              secondary="Number of Families"
            />
          )}
          
          {/* Products */}
          {(organization.hasSchoolLink || organization.hasEPublisher || organization.hasPaper) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Active Products
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {isValueTrue(organization.hasSchoolLink) && (
                  <Chip 
                    label="School Link" 
                    size="small" 
                    color="primary"
                    sx={{ fontSize: '0.75rem' }}
                  />
                )}
                {isValueTrue(organization.hasEPublisher) && (
                  <Chip 
                    label="ePublisher" 
                    size="small" 
                    color="secondary"
                    sx={{ fontSize: '0.75rem' }}
                  />
                )}
                {isValueTrue(organization.hasPaper) && (
                  <Chip 
                    label="Paper" 
                    size="small" 
                    color="info"
                    sx={{ fontSize: '0.75rem' }}
                  />
                )}
              </Box>
            </Box>
          )}
        </InfoCard>
      </Grid>
      
      {/* SchoolLink Information */}
      {isValueTrue(organization.hasSchoolLink) && (
        <Grid item xs={12} md={6}>
          <InfoCard 
            title="SchoolLink" 
            icon={<LinkIcon color="primary" />}
          >
            {organization.schoolLinkSubscribers !== undefined && organization.schoolLinkSubscribers !== null && (
              <InfoItem 
                icon={<PersonIcon fontSize="small" />}
                primary={organization.schoolLinkSubscribers.toString()}
                secondary="SchoolLink Subscribers"
              />
            )}
          </InfoCard>
        </Grid>
      )}
      
      {/* ePublisher Information */}
      {isValueTrue(organization.hasEPublisher) && (
        <Grid item xs={12} md={6}>
          <InfoCard 
            title="ePublisher" 
            icon={<CloudIcon color="secondary" />}
          >
            {organization.deploymentsPerTerm !== undefined && organization.deploymentsPerTerm !== null && (
              <InfoItem 
                icon={<CloudIcon fontSize="small" />}
                primary={organization.deploymentsPerTerm.toString()}
                secondary="Deployments per Term"
              />
            )}
            
            {(() => {
              const explicitSubs = organization.ePublisherSubscribers;
              const families = organization.numberOfFamilies;
              const effectiveSubs = (explicitSubs === 0 || explicitSubs === null || explicitSubs === undefined)
                ? (families ? families * 2 : null)
                : explicitSubs;
              const isEstimated = (explicitSubs === 0 || explicitSubs === null || explicitSubs === undefined) && !!families;
              return effectiveSubs !== null && effectiveSubs !== undefined ? (
                <InfoItem
                  icon={<PersonIcon fontSize="small" />}
                  primary={`${effectiveSubs}${isEstimated ? ' (est.)' : ''}`}
                  secondary="ePublisher Subscribers"
                />
              ) : null;
            })()}
            
            {organization.ePublisherCost !== undefined && organization.ePublisherCost !== null && (
              <InfoItem 
                icon={<PaymentIcon fontSize="small" />}
                primary={`$${parseFloat(organization.ePublisherCost).toFixed(2)}`}
                secondary="ePublisher Cost"
              />
            )}
          </InfoCard>
        </Grid>
      )}
      
      {/* Paper Information */}
      {isValueTrue(organization.hasPaper) && (
        <Grid item xs={12} md={6}>
          <InfoCard 
            title="Paper" 
            icon={<DescriptionIcon color="info" />}
          >
            {organization.paperCost !== undefined && organization.paperCost !== null && (
              <InfoItem 
                icon={<PaymentIcon fontSize="small" />}
                primary={`$${parseFloat(organization.paperCost).toFixed(2)}`}
                secondary="Paper Cost"
              />
            )}
            
            {organization.paperType && (
              <InfoItem 
                icon={<DescriptionIcon fontSize="small" />}
                primary={organization.paperType}
                secondary="Paper Type"
              />
            )}
          </InfoCard>
        </Grid>
      )}
      
      {/* Ad Statistics */}
      {organization.adStatistics && (
        <Grid item xs={12}>
          <AdStatistics 
            adStatistics={organization.adStatistics}
            hasPaper={organization.hasPaper}
            hasEPublisher={organization.hasEPublisher}
            hasSchoolLink={organization.hasSchoolLink}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default AdvertisingDetailsTab;

